import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaItemOverview.module.css";
import { ColorStyle } from "src/utils/Colors";
import { AgendaItemDragDrop } from "./dragdrop/AgendaItemDragDrop";
import { LOADING_TYPE } from "src/stores/assemblies/AssemblyStore";
import { AgendaItemUnlockModal } from "./modal/AgendaItemUnlockModal";

const AgendaItemOverviewBase = observer(() => {
    const { t } = useTranslation();
    const { agendaitemStore, assemblyStore, uiStore } = useContext(RootStoreContext);

    useEffect(() => {
        // Update navigation state using the store
        agendaitemStore.updateNavigation(assemblyStore.currentAssemblyTitle);

        // Load agenda items when component mounts
        agendaitemStore.loadAgendaItems();
    }, [agendaitemStore, assemblyStore.currentAssemblyTitle]);

    const handleDownloadClick = useCallback(async () => {
        agendaitemStore.toggleDropdown();
    }, [agendaitemStore]);

    const handleDropdownOptionClick = useCallback(
        (dropdownOption: number) => async (e: any) => {
            agendaitemStore.toggleDropdown(false);
            await assemblyStore.generateAssemblyRealestatePDF(
                agendaitemStore.currentAssemblyId || "",
                dropdownOption === 2
            );
            agendaitemStore.downloadAgendaItemPDF(dropdownOption);
        },
        [assemblyStore, agendaitemStore]
    );

    const handleSaveClick = useCallback(() => {
        agendaitemStore.saveAgendaItems();
    }, [agendaitemStore]);

    const saveFormButton =
        assemblyStore.loadingType !== LOADING_TYPE.NONE ? (
            <UI.RotatingSpinner noLogo={true} size={30} />
        ) : uiStore.isMobile ? (
            <UI.Icon
                onClick={agendaitemStore.agendaItemsHaveChanged ? handleSaveClick : undefined}
                icon={UI.SVGIcon.Save}
                color={ColorStyle("primary")}
                size={"large"}
                className={!agendaitemStore.agendaItemsHaveChanged ? styles.DisabledIcon : ""}
            />
        ) : (
            <UI.Button
                className={styles.SaveButton}
                onClick={handleSaveClick}
                label={t("labels.save").toString()}
                disabled={!agendaitemStore.agendaItemsHaveChanged}
            />
        );

    const DownloadButton = (
        <>
            {assemblyStore.isNewAssemblyPDFExists ? (
                <button
                    className={uiStore.isMobile ? styles.DownloadButtonMobile : styles.DownloadButton}
                    onClick={handleDownloadClick}
                    title={t("labels.downloads").toString()}
                >
                    <span className={styles.ButtonContent}>
                        {uiStore.isMobile ? undefined : <div>{t("labels.downloads")}</div>}
                        <UI.Icon
                            icon={agendaitemStore.isDropdownOpen ? UI.SVGIcon.ChevronUp : UI.SVGIcon.ChevronDown}
                            color={ColorStyle("primary")}
                            size={uiStore.isMobile ? 30 : 17}
                        />
                    </span>
                </button>
            ) : (
                <UI.RotatingSpinner noLogo={true} size={30} />
            )}
        </>
    );

    const onClickUnlockAgendaItems = useCallback(() => {
        agendaitemStore.setIsModalUnlockDisplayed(true);
    }, [agendaitemStore]);

    return (
        <React.Fragment>
            <UI.Container className={`${styles.AgendaItemContainer} pt-4`}>
                {/* Header Section */}
                <div className={styles.HeaderRow}>
                    <div className={styles.AgendaTitle}>{t("screens.realestate.assembly.agenda")}</div>
                    <div className={styles.DownloadButtonContainer}>
                        <div className={styles.HeaderButtons}>
                            {DownloadButton}
                            {saveFormButton}
                        </div>

                        {/* Dropdown */}
                        {agendaitemStore.isDropdownOpen && (
                            <ul className={styles.DropdownDownload}>
                                <li onClick={handleDropdownOptionClick(1)}>
                                    {t("screens.realestate.assembly.download_agendaPdf")}
                                </li>
                                <li onClick={handleDropdownOptionClick(2)}>
                                    {t("screens.realestate.assembly.download_agendaPdf_comments")}
                                </li>
                            </ul>
                        )}
                    </div>
                </div>
                {agendaitemStore.isAgendaItemLocked ? (
                    <>
                        <div className={styles.BlockedBanner}>
                            <div className={styles.BannerLeft}>
                                <UI.Icon className={styles.LockIcon} icon={UI.SVGIcon.Lock} size={"small"} />
                                <div className={styles.BannerText}>{t("screens.assemblies.agenda.locked_info")}</div>
                            </div>
                            <div className={styles.BannerLink} onClick={onClickUnlockAgendaItems}>
                                {t("screens.assemblies.agenda.unlock").toUpperCase()}
                            </div>
                        </div>
                        <AgendaItemUnlockModal />
                    </>
                ) : undefined}
                {agendaitemStore.currentAgendaItems.length < 0 ? (
                    <UI.RotatingSpinner noLogo={true} size={30} />
                ) : (
                    <AgendaItemDragDrop />
                )}
            </UI.Container>
        </React.Fragment>
    );
});

export const AgendaItemOverview = Sentry.withProfiler(AgendaItemOverviewBase);
